/**
 * CSV Parser Utilities
 *
 * Functions for parsing CSV files and generating templates.
 */

import Papa from 'papaparse';
import {
  CSVRowInput,
  RowValidationResult,
  validateRows,
  convertRowsToAPIFormat,
  CSV_COLUMNS,
  REQUIRED_COLUMNS,
} from '../../../utils/csvValidation';
import { BulkEventInput } from '../../../api/events';

/**
 * Result of parsing a CSV file
 */
export interface ParseResult {
  success: boolean;
  rows: CSVRowInput[];
  validationResults: RowValidationResult[];
  errors: string[];
}

/**
 * Parse a CSV file and validate its contents
 */
export function parseCSV(file: File): Promise<ParseResult> {
  return new Promise((resolve) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        const errors: string[] = [];

        // Check for parsing errors
        if (results.errors.length > 0) {
          results.errors.forEach((err) => {
            errors.push(`Row ${err.row}: ${err.message}`);
          });
        }

        // Check for required columns
        const headers = results.meta.fields || [];
        const missingColumns = REQUIRED_COLUMNS.filter(
          (col) => !headers.includes(col)
        );

        if (missingColumns.length > 0) {
          errors.push(
            `Missing required columns: ${missingColumns.join(', ')}`
          );
          resolve({
            success: false,
            rows: [],
            validationResults: [],
            errors,
          });
          return;
        }

        // Normalize row data (trim whitespace, handle empty strings)
        const rows = (results.data as Record<string, string>[]).map((row) => {
          const normalized: CSVRowInput = {
            name: row.name?.trim() || '',
            venue: row.venue?.trim() || '',
            network: row.network?.trim() || undefined,
            eventDate: row.eventDate?.trim() || '',
            callTime: row.callTime?.trim() || undefined,
            startTime: row.startTime?.trim() || undefined,
            endTime: row.endTime?.trim() || undefined,
            sport: row.sport?.trim() || undefined,
            eventType: row.eventType?.trim() || undefined,
            status: (row.status?.trim().toUpperCase() as CSVRowInput['status']) || undefined,
          };
          return normalized;
        });

        // Validate all rows
        const validationResults = validateRows(rows);

        resolve({
          success: errors.length === 0,
          rows,
          validationResults,
          errors,
        });
      },
      error: (error) => {
        resolve({
          success: false,
          rows: [],
          validationResults: [],
          errors: [`Failed to parse CSV: ${error.message}`],
        });
      },
    });
  });
}

/**
 * Convert validated rows to API format for submission
 */
export function prepareForAPI(
  validationResults: RowValidationResult[]
): BulkEventInput[] {
  // Only convert valid rows that have parsed data
  const validRows = validationResults
    .filter((r) => r.isValid && r.parsedData !== null)
    .map((r) => r.parsedData!);

  return convertRowsToAPIFormat(validRows);
}

/**
 * Generate and download a CSV template
 *
 * Shows examples with various date/time formats to demonstrate flexibility
 */
// RFC 4180 quoting for template cells — without it, a cell like
// "January 26, 2026" splits into two fields and the app's own upload
// validation rejects the freshly downloaded template.
function toCsvCell(value: string): string {
  return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
}

export function downloadTemplate(): void {
  const headers = CSV_COLUMNS.join(',');
  // Example 1: ISO date format, 24-hour time
  const exampleRow1 = [
    'Steelers vs Ravens',
    'Acrisure Stadium',
    'ESPN+',
    '2026-01-25',
    '3:00 PM',
    '4:00 PM',
    '7:00 PM',
    'Football',
    'Regular Season',
    'DRAFT',
  ]
    .map(toCsvCell)
    .join(',');
  // Example 2: Written date format, 12-hour time
  const exampleRow2 = [
    'Penguins vs Capitals',
    'PPG Paints Arena',
    'SNP',
    'January 26, 2026',
    '5:30 PM',
    '7:00 PM',
    '10:00 PM',
    'Hockey',
    'Regular Season',
    'DRAFT',
  ]
    .map(toCsvCell)
    .join(',');

  const csvContent = [headers, exampleRow1, exampleRow2].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = 'events_template.csv';
  link.click();
  URL.revokeObjectURL(url);
}

/**
 * Check if a file is a valid CSV
 */
export function isValidCSVFile(file: File): boolean {
  const validTypes = ['text/csv', 'application/vnd.ms-excel'];
  const validExtension = file.name.toLowerCase().endsWith('.csv');
  return validTypes.includes(file.type) || validExtension;
}

/**
 * Check if file size is within limit (default 5MB)
 */
export function isFileSizeValid(file: File, maxSizeMB = 5): boolean {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
}
